import type { ChangeEvent, DragEvent, ReactNode } from 'react';
import type { UploadedFile } from '../types';
import { useCallback, useRef, useState } from 'react';
import css from './upload-dropzone.module.scss';

export type UploadDropzoneProps = {
  // The consumer-side upload trigger — typically `startUpload`
  // returned from the package's `useUploadThing(endpoint)`. Consumers
  // can ignore the resolved value if they prefer the upload-complete
  // callback from `useUploadThing` for side-effects.
  startUpload: (files: File[]) => Promise<UploadedFile[] | undefined>;
  isUploading?: boolean;
  progress?: number;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  prompt?: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  className?: string;
};

export const UploadDropzone = ({
  startUpload,
  isUploading,
  progress,
  accept = 'image/*',
  multiple = true,
  disabled = false,
  prompt = 'Click to upload or drag and drop',
  hint,
  icon,
  className,
}: UploadDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isActive, setIsActive] = useState(false);

  const onFiles = useCallback(
    (fileList: FileList | File[] | null) => {
      if (!fileList || disabled) return;
      const files = Array.from(fileList);
      if (files.length === 0) return;
      void startUpload(files);
    },
    [disabled, startUpload]
  );

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    onFiles(event.target.files);
    event.target.value = '';
  };

  const onDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsActive(false);
    onFiles(event.dataTransfer.files);
  };

  const onDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsActive(true);
  };

  const onDragLeave = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsActive(false);
  };

  const rootClass = [
    css.root,
    isActive ? css.rootIsActive : '',
    disabled ? css.rootIsDisabled : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <input
        accept={accept}
        disabled={disabled}
        multiple={multiple}
        onChange={onChange}
        ref={inputRef}
        style={{ display: 'none' }}
        type="file"
      />
      <button
        className={rootClass}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        type="button"
      >
        {icon && <span className={css.icon}>{icon}</span>}
        <span className={css.prompt}>{prompt}</span>
        {hint && <span className={css.hint}>{hint}</span>}
        {isUploading && (
          <div className={css.progress}>
            <div
              className={css.progressFill}
              style={{ width: `${progress ?? 0}%` }}
            />
          </div>
        )}
      </button>
    </>
  );
};
